/**
 * ContactPage Component
 * 
 * Renders the contact page UI and handles form submission through EmailJS.
 * Includes:
 *  - Header with navigation + mobile menu
 *  - Static contact info
 *  - Contact form with validation and email sending
 *  - Footer links
 */

import React, { useState } from "react";
import emailjs from "emailjs-com";
import Footer from "../ui/Footer";
import Header from "../ui/Header";

const ContactPage = () => {

  // Form data and status message
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("");

  /**
   * Updates form input when any field changes.
   */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /**
   * Handles form submission:
   *  - Prevents page reload
   *  - Sends email using EmailJS
   *  - Shows status messages based on success/failure
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("Sending...");

    const templateParams = {
      name: form.name,
      email: form.email,
      subject: form.subject,
      message: form.message,
    };

    emailjs.send("service_e49oba1", "template_mcjpjkr", templateParams, "luuvKW7WoulQb4MP_")
      .then(() => {
        setStatus("Message sent — thank you!");
        setForm({ name: "", email: "", subject: "", message: "" });
      })
      .catch((error) => {
        console.error("Email send error:", error);
        setStatus("Failed to send. Try again later.");
      });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      {/* HEADER */}
      <Header />

      <main className="flex-1 px-6 py-16 max-w-6xl mx-auto w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <section>
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#0F172A] mb-4">
              Contact Us
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed mb-6">
              We're here to help. If you have any questions, requests, or feedback,
              feel free to reach out — we'll get back to you as soon as possible.
            </p>

            <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 shadow-sm space-y-4">
              <div>
                <p className="text-[#0F172A] text-base font-medium">Support</p>
                <p className="text-[#475569] text-sm">support@deebug.ai</p>
              </div>

              <div>
                <p className="text-[#0F172A] text-base font-medium">Business</p>
                <p className="text-[#475569] text-sm">partnerships@deebug.ai</p>
              </div>

              <div>
                <p className="text-[#0F172A] text-base font-medium">Feedback</p>
                <p className="text-[#475569] text-sm">We welcome suggestions to improve DEEBug.</p>
              </div>
            </div>
          </section>

          <section>
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#E2E8F0] rounded-xl p-6 shadow-sm space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-[#0F172A]">Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-md border border-[#E2E8F0] px-3 py-2 bg-white text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#A0E7FF]"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A]">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-md border border-[#E2E8F0] px-3 py-2 bg-white text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#A0E7FF]"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A]">Subject</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-md border border-[#E2E8F0] px-3 py-2 bg-white text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#A0E7FF]"
                  placeholder="Subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A]">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={6}
                  className="mt-2 block w-full rounded-md border border-[#E2E8F0] px-3 py-2 bg-white text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#A0E7FF]"
                  placeholder="How can we help?"
                  required
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold px-4 py-2 rounded-md transition"
                >
                  Send Message
                </button>

                <span className="text-sm text-[#475569]">{status}</span>
              </div>
            </form>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
